<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import {
  TemplateNormalizer,
  TemplatePreview,
} from 'dashboard/components-next/template-preview';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import {
  formatTemplateDate,
  formatTemplateLabel,
  formatTemplateLanguage,
  templateStatusClasses,
  templateStatusLabel,
  templateTypeKey,
} from './templateUtils';

const props = defineProps({
  template: {
    type: Object,
    default: null,
  },
  metrics: {
    type: Object,
    default: null,
  },
});

const metricRows = computed(() => {
  const metrics = props.metrics;
  if (!metrics) return [];

  const rows = [
    { key: 'SENT_LABEL', value: metrics.sent },
    {
      key: 'REPLIED_LABEL',
      value: metrics.sent
        ? `${metrics.replied} (${metrics.replyRate}%)`
        : metrics.replied,
      hint: 'REPLY_RATE_HINT',
    },
  ];

  // Delivered and read are only shown when the provider actually reports them; otherwise the row
  // would read as "nothing was delivered" when it means "nobody told us".
  if (metrics.hasDeliveryData) {
    rows.push(
      {
        key: 'DELIVERED_LABEL',
        value: `${metrics.delivered} (${metrics.deliveredRate}%)`,
      },
      { key: 'READ_LABEL', value: `${metrics.read} (${metrics.readRate}%)` }
    );
  }

  rows.push({
    key: 'FAILED_LABEL',
    value: metrics.sent
      ? `${metrics.failed} (${metrics.failedRate}%)`
      : metrics.failed,
    hint: 'FAILED_HINT',
  });

  if (metrics.viaCampaign) {
    rows.push({ key: 'CAMPAIGN_LABEL', value: metrics.viaCampaign });
  }
  if (metrics.lastSentAt) {
    rows.push({
      key: 'LAST_SENT_LABEL',
      value: formatTemplateDate(metrics.lastSentAt),
    });
  }
  return rows;
});

const i18n = useI18n();
const { t } = i18n;
const META_TEMPLATE_MANAGER_URL =
  'https://business.facebook.com/latest/whatsapp_manager/message_templates';
const TWILIO_TEMPLATE_MANAGER_URL =
  'https://console.twilio.com/us1/develop/sms/content-editor';

const panelRef = ref(null);
const showTestForm = ref(false);
const testPhoneNumber = ref('');
const isSendingTest = ref(false);

const platform = computed(() => props.template?.platform || PLATFORMS.WHATSAPP);
const normalizedTemplate = computed(() =>
  props.template
    ? TemplateNormalizer.normalize(props.template, platform.value)
    : null
);
const variables = computed(() => normalizedTemplate.value?.variables || {});
const managementUrl = computed(() => {
  if (platform.value === PLATFORMS.TWILIO) {
    return TWILIO_TEMPLATE_MANAGER_URL;
  }

  return props.template?.inboxes?.some(
    inbox => inbox.provider === 'whatsapp_cloud'
  )
    ? META_TEMPLATE_MANAGER_URL
    : null;
});
const managementLabel = computed(() =>
  platform.value === PLATFORMS.TWILIO
    ? t('WHATSAPP_TEMPLATE_MGMT.MANAGE_IN_TWILIO')
    : t('WHATSAPP_TEMPLATE_MGMT.MANAGE_IN_META')
);
const statusLabel = computed(() =>
  templateStatusLabel(props.template?.status, i18n)
);

// A grouped template can be synced from multiple WhatsApp inboxes sharing the
// same WABA; the test message is sent through whichever one is associated first.
const testInboxId = computed(() => props.template?.inboxes?.[0]?.id || null);
const canSendTest = computed(
  () => platform.value === PLATFORMS.WHATSAPP && !!testInboxId.value
);

const resetTestForm = () => {
  showTestForm.value = false;
  testPhoneNumber.value = '';
};

watch(() => props.template, resetTestForm);

const sendTestMessage = async ({ templateParams }) => {
  if (!testPhoneNumber.value.trim()) return;

  isSendingTest.value = true;
  try {
    await InboxesAPI.sendTestTemplateMessage(
      testInboxId.value,
      testPhoneNumber.value,
      templateParams
    );
    useAlert(t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.ERROR')
    );
  } finally {
    isSendingTest.value = false;
  }
};

const open = () => panelRef.value?.open();
const close = () => {
  panelRef.value?.close();
  resetTestForm();
};

defineExpose({ open, close });
</script>

<template>
  <SidePanel
    ref="panelRef"
    width="md"
    :title="template?.name"
    :description="$t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.DESCRIPTION')"
  >
    <div v-if="template" class="flex flex-col gap-6">
      <div
        class="flex items-center justify-center px-6 py-10 border rounded-xl min-h-80 border-n-weak bg-n-alpha-1"
      >
        <TemplatePreview
          :template="template"
          :variables="variables"
          :platform="platform"
        />
      </div>

      <div v-if="metrics">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.METRICS.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.METRICS.ALL_TIME') }}
        </p>
        <dl class="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-3 mt-4 text-sm">
          <template v-for="row in metricRows" :key="row.key">
            <dt
              class="text-n-slate-10"
              :title="
                row.hint
                  ? $t(`WHATSAPP_TEMPLATE_MGMT.METRICS.${row.hint}`)
                  : null
              "
            >
              {{ $t(`WHATSAPP_TEMPLATE_MGMT.METRICS.${row.key}`) }}
            </dt>
            <dd class="text-n-slate-12 tabular-nums">{{ row.value }}</dd>
          </template>
        </dl>
        <p
          v-if="!metrics.hasDeliveryData && metrics.sent"
          class="mt-3 text-sm text-n-slate-11"
        >
          {{ $t('WHATSAPP_TEMPLATE_MGMT.METRICS.NO_DELIVERY_DATA') }}
        </p>
      </div>

      <div>
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.DETAILS') }}
        </h3>
        <dl class="grid grid-cols-[8rem_1fr] gap-x-4 gap-y-3 mt-4 text-sm">
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.STATUS') }}
          </dt>
          <dd>
            <span
              class="inline-flex px-2 py-0.5 text-xs font-medium rounded-md"
              :class="templateStatusClasses(template.status)"
            >
              {{ statusLabel }}
            </span>
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.TYPE') }}
          </dt>
          <dd class="text-n-slate-12">
            {{
              $t(`WHATSAPP_TEMPLATE_MGMT.TYPES.${templateTypeKey(template)}`)
            }}
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.CATEGORY') }}
          </dt>
          <dd class="text-n-slate-12">
            {{ formatTemplateLabel(template.category) }}
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.LANGUAGE') }}
          </dt>
          <dd class="text-n-slate-12">
            {{ formatTemplateLanguage(template.language) }}
          </dd>
          <dt class="text-n-slate-10">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.INBOXES') }}
          </dt>
          <dd class="text-n-slate-12">{{ template.inboxNames }}</dd>
        </dl>
      </div>

      <div v-if="canSendTest" class="pt-6 border-t border-n-weak">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.TITLE') }}
          </h3>
          <Button
            v-if="!showTestForm"
            :label="$t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.OPEN')"
            icon="i-lucide-send"
            color="slate"
            size="sm"
            @click="showTestForm = true"
          />
        </div>

        <div v-if="showTestForm" class="flex flex-col gap-4 mt-4">
          <p class="text-xs text-n-slate-11">
            {{ $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.DESCRIPTION') }}
          </p>
          <Input
            v-model="testPhoneNumber"
            type="tel"
            :label="$t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.PHONE_LABEL')"
            :placeholder="
              $t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.PHONE_PLACEHOLDER')
            "
          />
          <WhatsAppTemplateParser
            :template="template"
            send-rendered-content
            @send-message="sendTestMessage"
          >
            <template
              #actions="{ sendMessage: submitTest, disabled: testInvalid }"
            >
              <Button
                class="w-full"
                :label="$t('WHATSAPP_TEMPLATE_MGMT.PREVIEW.SEND_TEST.SEND')"
                icon="i-lucide-send"
                :is-loading="isSendingTest"
                :disabled="
                  testInvalid || !testPhoneNumber.trim() || isSendingTest
                "
                @click="submitTest"
              />
            </template>
          </WhatsAppTemplateParser>
        </div>
      </div>
    </div>

    <template v-if="managementUrl" #footer>
      <a :href="managementUrl" target="_blank" rel="noopener noreferrer">
        <Button
          class="w-full"
          :label="managementLabel"
          icon="i-lucide-external-link"
          trailing-icon
        />
      </a>
    </template>
  </SidePanel>
</template>
